import type { ReactNode } from 'react'
import clsx from 'clsx'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Inbox, ScrollText, ShieldAlert } from 'lucide-react'
import {
  CHART_LEGEND_STYLE,
  CHART_TOOLTIP_PROPS,
  chartAxisProps,
  chartGridProps,
  chartRiskTierColor,
  chartSemanticProgressColor,
  chartSeriesColor,
} from '@/config/chartTheme'
import {
  AlertRail,
  AttentionTable,
  DashboardGrid,
  DashboardShell,
  DashboardTabs,
  FigureTable,
  FigureValue,
  FunnelPanel,
  GridCell,
  KpiStrip,
  MatrixPanel,
  MovementRail,
  NO_VALUE,
  Panel,
  QuickActions,
  ScorecardPanel,
  figureToneClass,
  formatFigure,
  scorecardMetrics,
  useDashboard,
  useDashboardView,
} from '@/components/dashboards'
import type { DashboardTab } from '@/components/dashboards'
import type {
  AlertItem,
  AlertSeverity,
  DashboardPanelData,
  DrillDescriptor,
  Figure,
  FigureFormat,
  FigureTableRow,
  FigureTone,
  MovementItem,
} from '@/types/dashboards'

/**
 * The Risk Mgmt board (formerly Legal/Compliance; design spec 2026-08-16, §8).
 *
 * Louis opens this every morning, so the whole legal and compliance posture of
 * the firm lands in one window: eight headline figures, the compliance-plan
 * bar, then the posture matrix, exposure trend, scorecard and the standing
 * right rail, then closings / obligations / attention, then the four deep
 * panels.
 *
 * The page renders the payload and computes nothing. Every number, tone and
 * drill descriptor is server-derived (spec §6); where this endpoint ships no
 * data for a block the reference layout carries, the block says so rather than
 * borrowing a neighbouring figure or showing an empty frame that reads as zero.
 */

// ── Wire shapes ──────────────────────────────────────────────────────────────
// The board document is keyed by panel name and each panel carries this board's
// own shape, which is wider than the kit's generic panel union — the panels are
// adapted to the kit's props at the render site below.

interface HeadlinePanel {
  tiles: Figure[]
  openSanctionsAlerts: number | null
}

interface PlanProgressPanel {
  planYear: number
  total: Figure
  complete: Figure
  progress: Figure
}

interface PostureRow {
  domain: string
  label: string
  score: Figure
  controls: Figure
  remediation: Figure
  evidenceGaps: Figure
  criticalFailures: Figure
}

interface PosturePanel {
  rows: PostureRow[]
}

interface ExposurePoint {
  month: string
  label: string
  openMatters: number | null
  claimed: number | null
  reserved: number | null
  uninsured: number | null
}

interface ExposureTrendPanel {
  points: ExposurePoint[]
  closureRate: Figure
  openedInWindow: number
  closedInWindow: number
}

interface ScorecardWirePanel {
  metrics: Figure[]
  overall: Figure
  /** How much of the scorecard the overall ring actually averages.
   *
   *  A 0% overall carries very different weight when five metrics scored it and
   *  when one did — and on a partition that has only started to fill, four of
   *  the five are routinely unmeasurable. Optional: a board built before the
   *  basis line landed ships no footnote. */
  footnote?: string
}

interface HealthPanel {
  rails: Figure[]
}

interface CriticalAlertsPanel {
  alerts: Figure[]
  privacyObligation: string | null
}

interface MovementWireRow {
  source: string
  title: string
  change: string | null
  at: string | null
  actor: string | null
  drill?: DrillDescriptor | null
}

interface MovementWirePanel {
  rows: MovementWireRow[]
}

interface QuickActionWireRow {
  label: string
  count: number | null
  drill?: DrillDescriptor | null
}

interface QuickActionsWirePanel {
  actions: QuickActionWireRow[]
}

interface ReadinessRow {
  loanId: string | null
  deal: string | null
  amount: number | null
  counsel: string | null
  kycStatus: string | null
  docsCompletePct: number | null
  openExceptions: number | null
  targetCloseAt: string | null
  readinessStatus: string | null
  unresolvedConditions: string | null
  href: string | null
  drill?: DrillDescriptor | null
}

interface ReadinessPanel {
  summary: Figure[]
  rows: ReadinessRow[]
}

interface MatrixWireRow {
  category: string
  label: string
  cells: Figure[]
}

interface ObligationsPanel {
  buckets: { key: string; label: string }[]
  rows: MatrixWireRow[]
}

interface ExceptionsPanel {
  statuses: { key: string; label: string }[]
  rows: MatrixWireRow[]
  precedentRisk: Figure
}

interface AttentionWireRow {
  source: string
  title: string
  detail: string | null
  amount: number | null
  daysToDeadline: number | null
  severity: string | null
  action: string
  drill?: DrillDescriptor | null
  href?: string | null
}

interface AttentionWirePanel {
  rows: AttentionWireRow[]
}

interface GovernanceRow {
  id: string
  title: string
  category: string
  status: string
  scheduledAt: string | null
  exposureAmount: number | null
  deal: string | null
  href: string | null
  drill?: DrillDescriptor | null
}

interface GovernancePanel {
  rows: GovernanceRow[]
}

interface AlertAgingBucket {
  key: string
  label: string
  count: Figure
}

/** How long open sanctions alerts have sat, in days since they were raised. */
interface AlertAgingPanel {
  buckets: AlertAgingBucket[]
  openAlerts: Figure
}

interface KycPanel {
  parties: Figure[]
  screenings: Figure[]
  coverage: Figure
  eddOpen: Figure
  alertAging: AlertAgingPanel
}

interface AuditRow {
  id: string
  name: string
  auditType: string | null
  auditor: string | null
  status: string | null
  completionPct: number | null
  dueAt: string | null
  openFindings: Figure
  drill?: DrillDescriptor | null
}

interface AuditsPanel {
  rows: AuditRow[]
  summary: Figure[]
}

interface MatterRow {
  id: string
  matterNumber: string
  title: string
  category: string
  status: string
  severity: string | null
  counterparty: string | null
  leadCounsel: string | null
  claimed: number | null
  uninsured: number | null
  nextMilestoneAt: string | null
  deal: string | null
  href: string | null
  drill?: DrillDescriptor | null
}

interface MattersPanel {
  rows: MatterRow[]
  byCategory: Figure[]
}

interface CounselFirmRow {
  firmId: string | null
  firm: string | null
  isPreferred: boolean
  spend: Figure
  invoices: number | null
  offRateInvoices: Figure
}

/** A month with no invoice carries `spend: null`, not 0 — nothing billed and
 *  nothing recorded are different statements, so the bar is absent, not zero. */
interface SpendPoint {
  month: string
  label: string
  spend: number | null
  budget: number | null
  forecast: number | null
  drill?: DrillDescriptor | null
}

interface CounselPanel {
  rows: CounselFirmRow[]
  summary: Figure[]
  spendVsBudget: { points: SpendPoint[] }
}

/** A panel key the endpoint has not sent yet reads as absent, not as empty. */
function readPanel<T>(panels: Record<string, DashboardPanelData>, key: string): T | null {
  return (panels[key] as unknown as T | undefined) ?? null
}

// ── Tabs ─────────────────────────────────────────────────────────────────────
// Three sub-pages. Matters, exposure, attention and closings live on Overview
// so the morning board is one page, not a second 2×2 of empty cards. A stale
// ?view=matters deep link falls back to Overview (unknown ids do).

const TABS: DashboardTab[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'compliance', label: 'Compliance & Controls' },
  { id: 'kyc', label: 'KYC, Counsel & Governance' },
]

// ── Presentation maps ────────────────────────────────────────────────────────

const READINESS_TONE: Record<string, FigureTone> = {
  clear: 'positive',
  watch: 'warning',
  hold: 'warning',
  at_risk: 'negative',
}

const SEVERITY_TONE: Record<string, FigureTone> = {
  high: 'negative',
  medium: 'warning',
  low: 'neutral',
}

type SeverityTier = 'high' | 'medium' | 'low'

const SEVERITY_TIERS: SeverityTier[] = ['high', 'medium', 'low']

function severityTier(value: string | null): SeverityTier | null {
  return SEVERITY_TIERS.find((tier) => tier === value) ?? null
}

const OPEN_MATTERS_SERIES = 'Open matters'

const EXPOSURE_SERIES: { key: keyof ExposurePoint; label: string; color: string }[] = [
  { key: 'claimed', label: 'Claimed', color: chartSeriesColor(7) },
  { key: 'reserved', label: 'Reserved', color: chartSeriesColor(3) },
  { key: 'uninsured', label: 'Uninsured', color: chartSeriesColor(0) },
]

/** Invoiced spend is the bar; plan and forecast are reference lines over it. */
const SPEND_LINES: { key: keyof SpendPoint; label: string; color: string; dash?: string }[] = [
  { key: 'budget', label: 'Budget', color: chartSeriesColor(7), dash: '4 3' },
  { key: 'forecast', label: 'Forecast', color: chartSeriesColor(0) },
]

// ── Formatting ───────────────────────────────────────────────────────────────

function fig(
  label: string,
  value: number | string | null,
  format: FigureFormat,
  options: { drill?: DrillDescriptor | null; tone?: FigureTone } = {},
): Figure {
  const built: Figure = { label, value, format }
  if (options.tone) built.tone = options.tone
  if (options.drill) built.drill = options.drill
  return built
}

function shortDate(value: string | null): string | null {
  if (!value) return null
  const at = Date.parse(value)
  if (!Number.isFinite(at)) return null
  return new Date(at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function humanize(value: string | null): string | null {
  return value ? value.replace(/_/g, ' ') : null
}

/** Days-to-deadline as the rail reads it. Past due counts up, not down. */
function deadlineLabel(days: number | null): string | undefined {
  if (days == null) return undefined
  const whole = Math.round(Math.abs(days))
  return days < 0 ? `${whole}d late` : `${whole}d`
}

function isBlank(figure: Figure | null | undefined): boolean {
  return figure == null || figure.value === null || figure.value === ''
}

function alertSeverity(tone?: FigureTone): AlertSeverity {
  if (tone === 'negative') return 'critical'
  if (tone === 'warning') return 'high'
  return 'low'
}

// ── Local blocks ─────────────────────────────────────────────────────────────
// Shapes the shared kit does not cover: the rail bar list, the label/value stat
// list, the compact footer strip, and the two list panels whose rows are
// records rather than Figures.

function RailBars({ figures, compact = false }: { figures: Figure[]; compact?: boolean }) {
  return (
    <div className={compact ? 'space-y-1' : 'space-y-2'}>
      {figures.map((figure) => {
        const score = typeof figure.value === 'number' ? Math.max(0, Math.min(100, figure.value)) : null
        return (
          <div key={figure.label} className="space-y-1">
            <div className="flex items-baseline justify-between gap-2">
              <span className="truncate text-[11px] text-stone-300" title={figure.label}>
                {figure.label}
              </span>
              <span
                className={clsx(
                  'flex-shrink-0 font-mono text-[11px] tabular-nums',
                  figureToneClass(figure.tone),
                )}
              >
                <FigureValue figure={figure} align="right" />
              </span>
            </div>
            <div className="chart-viz-track h-1.5 w-full rounded-full">
              {score != null && (
                <div
                  className="chart-viz-bar chart-viz-bar--rounded h-full"
                  style={{ width: `${score}%`, background: chartSemanticProgressColor(score) }}
                />
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function StatList({ figures, className }: { figures: Figure[]; className?: string }) {
  return (
    <dl className={clsx('space-y-1', className)}>
      {figures.map((figure) => (
        <div key={figure.label} className="flex items-baseline justify-between gap-2">
          <dt className="truncate text-[11px] text-stone-400" title={figure.label}>
            {figure.label}
          </dt>
          <dd
            className={clsx(
              'flex-shrink-0 font-mono text-[11px] tabular-nums',
              figureToneClass(figure.tone),
            )}
          >
            <FigureValue figure={figure} align="right" />
          </dd>
        </div>
      ))}
    </dl>
  )
}

function FooterFigures({ figures }: { figures: Figure[] }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      {figures.map((figure) => (
        <span key={figure.label} className="inline-flex items-baseline gap-1">
          <span className="text-stone-600">{figure.label}</span>
          <span className={clsx('font-mono tabular-nums', figureToneClass(figure.tone))}>
            <FigureValue figure={figure} />
          </span>
        </span>
      ))}
    </div>
  )
}

function SubHeading({ children }: { children: ReactNode }) {
  return <div className="terminal-kicker !text-[8px]">{children}</div>
}

/** One quadrant inside the overview matters module — a heading plus either a
 *  one-line honest empty or the live body. Not a full-bleed Panel. */
function CompactSection({
  title,
  kicker,
  isEmpty,
  emptyTitle,
  emptyDescription,
  children,
}: {
  title: string
  kicker?: ReactNode
  isEmpty: boolean
  emptyTitle: string
  emptyDescription: string
  children?: ReactNode
}) {
  return (
    <section className="flex min-h-0 min-w-0 flex-col rounded-md border border-amber-400/10 bg-black/20">
      <header className="flex flex-shrink-0 items-baseline justify-between gap-2 px-2.5 py-1.5">
        <h3 className="truncate text-[10px] font-semibold uppercase tracking-wider text-stone-400">
          {title}
        </h3>
        {kicker ? <div className="flex-shrink-0 text-[10px] text-stone-500">{kicker}</div> : null}
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto px-2.5 pb-2 scrollbar-thin">
        {isEmpty ? (
          <p className="text-[11px] leading-snug text-stone-500">
            <span className="font-medium text-stone-400">{emptyTitle}. </span>
            {emptyDescription}
          </p>
        ) : (
          children
        )}
      </div>
    </section>
  )
}

/** Top material matters. Bar width is a share of the largest claimed exposure
 *  on the list, so a matter with no recorded exposure draws no bar at all. */
function MatterBars({ rows }: { rows: MatterRow[] }) {
  const peak = Math.max(...rows.map((row) => row.claimed ?? 0), 1)

  return (
    <div className="space-y-1.5">
      {rows.map((row) => {
        const width = row.claimed == null ? 0 : (row.claimed / peak) * 100
        const tier = severityTier(row.severity)
        return (
          <div key={row.id} className="space-y-1">
            <div className="flex items-baseline justify-between gap-2">
              <span className="min-w-0 truncate text-[11px] text-stone-200" title={row.title}>
                <FigureValue
                  figure={fig(row.matterNumber, row.title, 'text', { drill: row.drill })}
                />
              </span>
              <span className="flex-shrink-0 font-mono text-[11px] tabular-nums text-stone-100">
                <FigureValue
                  figure={fig('Claimed exposure', row.claimed, 'currencyM', { drill: row.drill })}
                  align="right"
                />
              </span>
            </div>
            <div className="chart-viz-track h-1.5 w-full rounded-sm">
              {width > 0 && tier && (
                <div
                  className="chart-viz-bar chart-viz-bar--rounded-sm h-full"
                  style={{ width: `${width}%`, background: chartRiskTierColor(tier) }}
                />
              )}
            </div>
            <div className="flex items-baseline gap-2 text-[10px] text-stone-500">
              <span className="capitalize">{humanize(row.category) ?? NO_VALUE}</span>
              <span className="truncate">{row.counterparty ?? row.leadCounsel ?? row.deal ?? ''}</span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function SeverityLegend() {
  return (
    <div className="flex items-center gap-2.5">
      {SEVERITY_TIERS.map((tier) => (
        <span key={tier} className="inline-flex items-center gap-1 capitalize">
          <span
            className="h-1.5 w-3 rounded-sm"
            style={{ background: chartRiskTierColor(tier) }}
            aria-hidden="true"
          />
          {tier}
        </span>
      ))}
    </div>
  )
}

/** Today's governance docket as tiles. The item's own drill opens its records. */
function GovernanceTiles({ rows }: { rows: GovernanceRow[] }) {
  return (
    <div className="grid grid-cols-2 gap-1.5">
      {rows.map((row) => (
        <div key={row.id} className="surface-panel min-w-0 rounded-md px-2 py-1.5">
          <SubHeading>{humanize(row.category) ?? NO_VALUE}</SubHeading>
          <div className="mt-0.5 truncate text-[11px] text-stone-200" title={row.title}>
            <FigureValue figure={fig(row.title, row.title, 'text', { drill: row.drill })} />
          </div>
          <div className="mt-0.5 flex items-baseline justify-between gap-2 text-[10px] text-stone-500">
            <span className="capitalize">{humanize(row.status) ?? NO_VALUE}</span>
            <span className="font-mono">{shortDate(row.scheduledAt) ?? NO_VALUE}</span>
          </div>
          {row.exposureAmount != null && (
            <div className="mt-0.5 font-mono text-[11px] tabular-nums text-amber-200">
              <FigureValue
                figure={fig('Exposure', row.exposureAmount, 'currencyM', { drill: row.drill })}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

/** Outside counsel, ranked by year-to-date spend. */
function FirmList({ rows }: { rows: CounselFirmRow[] }) {
  return (
    <div className="space-y-1">
      {rows.map((row, index) => (
        <div
          key={row.firmId ?? `${row.firm}-${index}`}
          className="flex items-baseline justify-between gap-2"
        >
          <span className="flex min-w-0 items-baseline gap-1.5">
            <span className="w-3 flex-shrink-0 font-mono text-[9px] text-stone-600">
              {index + 1}
            </span>
            <span className="truncate text-[11px] text-stone-300" title={row.firm ?? undefined}>
              {row.firm ?? NO_VALUE}
            </span>
            {row.isPreferred && (
              <span className="flex-shrink-0 rounded border border-amber-400/20 px-1 font-mono text-[8px] uppercase tracking-wider text-amber-300/80">
                Pref
              </span>
            )}
          </span>
          <span className="flex flex-shrink-0 items-baseline gap-2 font-mono text-[11px] tabular-nums">
            <span className={figureToneClass(row.offRateInvoices.tone)}>
              <FigureValue figure={row.offRateInvoices} align="right" />
            </span>
            <span className="text-stone-100">
              <FigureValue figure={row.spend} align="right" />
            </span>
          </span>
        </div>
      ))}
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function LegalComplianceDashboard() {
  const board = useDashboard('legal')
  const { panels, isLoading, error } = board
  const retry = () => void board.refetch()
  const [active, setActive] = useDashboardView(TABS)

  const headline = readPanel<HeadlinePanel>(panels, 'headline')
  const planProgress = readPanel<PlanProgressPanel>(panels, 'planProgress')
  const posture = readPanel<PosturePanel>(panels, 'posture')
  const exposure = readPanel<ExposureTrendPanel>(panels, 'exposureTrend')
  const scorecard = readPanel<ScorecardWirePanel>(panels, 'scorecard')
  const health = readPanel<HealthPanel>(panels, 'health')
  const criticalAlerts = readPanel<CriticalAlertsPanel>(panels, 'criticalAlerts')
  const movement = readPanel<MovementWirePanel>(panels, 'recentMovement')
  const quickActions = readPanel<QuickActionsWirePanel>(panels, 'quickActions')
  const readiness = readPanel<ReadinessPanel>(panels, 'transactionReadiness')
  const obligations = readPanel<ObligationsPanel>(panels, 'obligations')
  const exceptions = readPanel<ExceptionsPanel>(panels, 'exceptions')
  const attention = readPanel<AttentionWirePanel>(panels, 'headAttention')
  const governance = readPanel<GovernancePanel>(panels, 'governance')
  const kyc = readPanel<KycPanel>(panels, 'kyc')
  const audits = readPanel<AuditsPanel>(panels, 'audits')
  const matters = readPanel<MattersPanel>(panels, 'matters')
  const counsel = readPanel<CounselPanel>(panels, 'counsel')

  const planPct =
    planProgress && typeof planProgress.progress.value === 'number'
      ? Math.max(0, Math.min(100, planProgress.progress.value))
      : null

  // The sub-tiles read the most recent point of the shipped series rather than
  // re-aggregating it — the last month the server recorded is the current state.
  const exposureLatest = exposure?.points[exposure.points.length - 1] ?? null
  const exposureLatestTiles: Figure[] = [
    fig('Claimed', exposureLatest?.claimed ?? null, 'currencyM'),
    fig('Reserved', exposureLatest?.reserved ?? null, 'currencyM'),
    fig('Uninsured', exposureLatest?.uninsured ?? null, 'currencyM'),
    fig('Open matters', exposureLatest?.openMatters ?? null, 'count'),
  ]

  const postureRows: FigureTableRow[] = (posture?.rows ?? []).map((row) => ({
    id: row.domain,
    cells: [
      fig('Domain', row.label, 'text'),
      row.controls,
      row.remediation,
      row.evidenceGaps,
      row.criticalFailures,
      row.score,
    ],
  }))

  const readinessRows: FigureTableRow[] = (readiness?.rows ?? []).map((row, index) => ({
    id: row.loanId ?? `${row.deal}-${index}`,
    href: row.href ?? undefined,
    cells: [
      fig('Deal', row.deal ?? row.loanId, 'text', { drill: row.drill }),
      fig('Amount', row.amount, 'currencyM'),
      fig('Counsel', row.counsel, 'text'),
      fig('KYC', humanize(row.kycStatus), 'text'),
      fig('Docs', row.docsCompletePct, 'pct'),
      fig('Exceptions', row.openExceptions, 'count'),
      fig('Close', shortDate(row.targetCloseAt), 'text'),
      fig('Status', humanize(row.readinessStatus), 'text', {
        tone: row.readinessStatus ? READINESS_TONE[row.readinessStatus] : undefined,
      }),
    ],
  }))

  const auditRows: FigureTableRow[] = (audits?.rows ?? []).map((row) => ({
    id: row.id,
    cells: [
      fig('Item', row.name, 'text', { drill: row.drill }),
      fig('Type', humanize(row.auditType), 'text'),
      fig('Status', humanize(row.status), 'text'),
      fig('Complete', row.completionPct, 'pct'),
      row.openFindings,
      fig('Due', shortDate(row.dueAt), 'text'),
    ],
  }))

  const alertItems: AlertItem[] = (criticalAlerts?.alerts ?? []).map((alert) => ({
    id: alert.label,
    severity: alertSeverity(alert.tone),
    title: alert.label,
    figure: alert,
    drill: alert.drill,
  }))

  const movementItems: MovementItem[] = (movement?.rows ?? []).map((row, index) => ({
    // Index always participates: source+timestamp is not unique — two matters
    // whose events land on the same seeded hour collide, and React drops one
    // of the colliding rows, so a real status change silently vanishes.
    id: `${row.source}-${index}-${row.at ?? ''}`,
    occurredAt: row.at ?? '',
    title: row.title,
    detail: [row.change, row.actor].filter(Boolean).join(' · ') || undefined,
    drill: row.drill ?? undefined,
  }))

  const scoreHasRows = scorecard?.metrics.some((metric) => metric.value != null) ?? false
  const healthHasRows = health?.rails.some((rail) => rail.value != null) ?? false
  const exposureEmpty =
    !exposure ||
    exposure.points.every((point) => point.claimed == null && !point.openMatters)
  const mattersEmpty =
    !matters || (matters.rows.length === 0 && matters.byCategory.length === 0)

  // Overview is a 4-column grid: a compact scorecard+health cell, three 1×1
  // rails, then one module that holds exposure / attention / matters / readiness.
  const renderActiveGrid = () => {
    if (active === 'overview') {
      return (
        <DashboardGrid rows={4}>
          <GridCell panelId="scorecard" panelLabel="Legal & Compliance Scorecard" col={1} row={1}>
            <Panel
              title="Legal & Compliance Scorecard"
              className="min-w-0 flex-1"
              isLoading={isLoading}
              isEmpty={!scoreHasRows && !healthHasRows}
              emptyTitle="Scorecard not computable"
              emptyDescription="Health is not computable either. Each metric and rail is derived from records; this partition has no compliance, matter, closing or governance rows to score."
              compactEmpty
              scroll
              bodyClassName="flex flex-col gap-2"
            >
              {scoreHasRows && scorecard && (
                <ScorecardPanel
                  data={{
                    overall: scorecard.overall,
                    metrics: scorecardMetrics(scorecard.metrics),
                    footnote: scorecard.footnote,
                  }}
                  dialSize={52}
                  // Full height inside the scrolling body: shrunk to fit, its
                  // rows ran under the Health rails below.
                  className="flex-shrink-0 gap-2"
                />
              )}
              {!scoreHasRows && (
                <p className="text-[11px] leading-snug text-stone-500">
                  Scorecard not computable — none of the five inputs has rows in this
                  partition.
                </p>
              )}
              <div className="flex-shrink-0 space-y-1">
                <SubHeading>Health</SubHeading>
                {healthHasRows && health ? (
                  <RailBars figures={health.rails} compact />
                ) : (
                  <p className="text-[11px] leading-snug text-stone-500">
                    Health not computable — no compliance, matter, closing or governance
                    records to score.
                  </p>
                )}
              </div>
            </Panel>
          </GridCell>

          <GridCell panelId="critical-alerts" panelLabel="Critical Alerts" col={1} row={1}>
            <Panel
              title="Critical Alerts"
              className="min-w-0 flex-1"
              kicker={
                criticalAlerts?.privacyObligation
                  ? criticalAlerts.privacyObligation
                  : undefined
              }
              isLoading={isLoading}
              isEmpty={alertItems.length === 0}
              emptyTitle="No alert predicates evaluated"
              emptyDescription="Alerts are hard predicates over records; this partition has none to evaluate."
              emptyIcon={ShieldAlert}
              compactEmpty
              skeletonRows={4}
              scroll
            >
              <AlertRail alerts={alertItems} />
            </Panel>
          </GridCell>

          <GridCell panelId="recent-movement" panelLabel="Recent Movement" col={1} row={1}>
            <Panel
              title="Recent Movement"
              className="min-w-0 flex-1"
              isLoading={isLoading}
              isEmpty={movementItems.length === 0}
              emptyTitle="No status changes"
              emptyDescription="Nothing has moved across matters, obligations, exceptions or findings."
              compactEmpty
              skeletonRows={4}
              scroll
            >
              <MovementRail movements={movementItems} />
            </Panel>
          </GridCell>

          <GridCell panelId="quick-actions" panelLabel="Executive Quick Actions" col={1} row={1}>
            <Panel
              title="Executive Quick Actions"
              className="min-w-0 flex-1"
              isLoading={isLoading}
              isEmpty={(quickActions?.actions.length ?? 0) === 0}
              emptyTitle="No queued actions"
              emptyDescription="Quick actions carry live counts; none of the queues has records."
              compactEmpty
              skeletonRows={3}
              scroll
            >
              <QuickActions
                actions={(quickActions?.actions ?? []).map((action) => ({
                  id: action.label,
                  label: action.label,
                  count: action.count,
                  drill: action.drill ?? undefined,
                }))}
              />
            </Panel>
          </GridCell>

          <GridCell panelId="matters-exposure-closings" panelLabel="Matters, exposure & closings" col={4} row={3}>
            <Panel
              title="Matters, exposure & closings"
              className="min-w-0 flex-1"
              isLoading={isLoading}
              bodyClassName="flex min-h-0 flex-col"
            >
              <div className="grid min-h-0 flex-1 grid-cols-2 grid-rows-2 gap-2">
              <CompactSection
                title="Legal & Regulatory Exposure Trend"
                kicker={exposure ? `${exposure.points.length} months` : undefined}
                isEmpty={exposureEmpty}
                emptyTitle="No exposure history"
                emptyDescription="The trend is folded from the matter event trail; no events have been recorded in this partition."
              >
                {exposure && (
                  <div className="flex h-full min-h-0 flex-col gap-2">
                    <div className="grid grid-cols-4 gap-1.5">
                      {exposureLatestTiles.map((tile) => (
                        <div key={tile.label} className="surface-panel min-w-0 rounded-md px-2 py-1">
                          <SubHeading>{tile.label}</SubHeading>
                          <div className="mt-0.5 truncate font-mono text-[12px] tabular-nums text-stone-100">
                            <FigureValue figure={tile} />
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="min-h-[120px] flex-1">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                          data={exposure.points}
                          margin={{ top: 4, right: 2, bottom: 0, left: 0 }}
                        >
                          <CartesianGrid {...chartGridProps()} />
                          <XAxis dataKey="label" {...chartAxisProps(9)} interval="preserveStartEnd" />
                          <YAxis
                            yAxisId="amount"
                            width={46}
                            {...chartAxisProps(9)}
                            tickFormatter={(value: number) =>
                              formatFigure({ label: 'Exposure', value, format: 'currencyM' })
                            }
                          />
                          <YAxis
                            yAxisId="count"
                            orientation="right"
                            width={26}
                            allowDecimals={false}
                            {...chartAxisProps(9)}
                          />
                          <Tooltip
                            {...CHART_TOOLTIP_PROPS}
                            formatter={(value: number, name: string): [string, string] => [
                              name === OPEN_MATTERS_SERIES
                                ? formatFigure({ label: name, value, format: 'count' })
                                : formatFigure({ label: name, value, format: 'currencyM' }),
                              name,
                            ]}
                          />
                          <Legend iconType="plainline" wrapperStyle={CHART_LEGEND_STYLE} />
                          {EXPOSURE_SERIES.map((series) => (
                            <Line
                              key={series.key}
                              yAxisId="amount"
                              type="monotone"
                              dataKey={series.key}
                              name={series.label}
                              stroke={series.color}
                              strokeWidth={1.6}
                              dot={false}
                              connectNulls={false}
                              isAnimationActive={false}
                            />
                          ))}
                          <Line
                            yAxisId="count"
                            type="monotone"
                            dataKey="openMatters"
                            name={OPEN_MATTERS_SERIES}
                            stroke={chartSeriesColor(5)}
                            strokeWidth={1.2}
                            strokeDasharray="3 3"
                            dot={false}
                            connectNulls={false}
                            isAnimationActive={false}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                    <FooterFigures
                      figures={[
                        exposure.closureRate,
                        fig('Opened', exposure.openedInWindow, 'count'),
                        fig('Closed', exposure.closedInWindow, 'count'),
                      ]}
                    />
                  </div>
                )}
              </CompactSection>

              <CompactSection
                title="Need Attention"
                kicker={attention ? `top ${attention.rows.length}` : undefined}
                isEmpty={(attention?.rows.length ?? 0) === 0}
                emptyTitle="Nothing above the threshold"
                emptyDescription="No closing, matter, covenant, obligation, exception or finding scored onto the list."
              >
                {attention && (
                  <AttentionTable
                    data={{
                      items: attention.rows.map((row, index) => ({
                        id: `${row.source}-${index}`,
                        rank: index + 1,
                        title: row.title,
                        subtitle: [humanize(row.source), row.detail]
                          .filter(Boolean)
                          .join(' · '),
                        figure:
                          row.amount == null
                            ? undefined
                            : fig('Exposure', row.amount, 'currencyM'),
                        dueLabel: deadlineLabel(row.daysToDeadline),
                        tone: row.severity ? SEVERITY_TONE[row.severity] : undefined,
                        action: {
                          label: row.action,
                          drill: row.drill ?? undefined,
                          href: row.href ?? undefined,
                        },
                      })),
                    }}
                  />
                )}
              </CompactSection>

              <CompactSection
                title="Matters, Disputes & Workouts"
                kicker={<SeverityLegend />}
                isEmpty={mattersEmpty}
                emptyTitle="No open matters"
                emptyDescription="Litigation, workouts, investigations, claims and legal holds appear here while open."
              >
                {matters && (
                  <div className="space-y-2.5">
                    {matters.byCategory.length > 0 && (
                      <div className="space-y-1">
                        <SubHeading>Open by category</SubHeading>
                        <StatList figures={matters.byCategory} />
                      </div>
                    )}
                    {matters.rows.length > 0 && (
                      <div className="space-y-1.5">
                        <SubHeading>Top material matters</SubHeading>
                        <MatterBars rows={matters.rows} />
                      </div>
                    )}
                  </div>
                )}
              </CompactSection>

              <CompactSection
                title="Transaction Legal Readiness"
                kicker={readiness ? `${readiness.rows.length} closings` : undefined}
                isEmpty={readinessRows.length === 0}
                emptyTitle="No closings in the window"
                emptyDescription="Deals appear here once they carry a target close date inside the readiness horizon."
              >
                <div className="flex h-full min-h-0 flex-col">
                  <FigureTable
                    columns={[
                      { key: 'deal', label: 'Deal', align: 'left' },
                      { key: 'amount', label: 'Amount', align: 'right' },
                      { key: 'counsel', label: 'Counsel', align: 'left' },
                      { key: 'kyc', label: 'KYC', align: 'left' },
                      { key: 'docs', label: 'Docs', align: 'right' },
                      { key: 'exceptions', label: 'Exc', align: 'right' },
                      { key: 'close', label: 'Close', align: 'right' },
                      { key: 'status', label: 'Status', align: 'left' },
                    ]}
                    rows={readinessRows}
                  />
                  {readiness && (
                    <div className="mt-auto border-t border-amber-400/[0.06] pt-1.5 text-[10px] text-stone-500">
                      <FooterFigures figures={readiness.summary} />
                    </div>
                  )}
                </div>
              </CompactSection>
              </div>
            </Panel>
          </GridCell>
        </DashboardGrid>
      )
    }

    if (active === 'compliance') {
      return (
        <DashboardGrid rows={4}>
          <GridCell panelId="compliance-posture" panelLabel="Enterprise Compliance Posture" col={2} row={2}>
            <Panel
              title="Enterprise Compliance Posture"
              className="min-w-0 flex-1"
              kicker={posture ? `${posture.rows.length} domains` : undefined}
              isLoading={isLoading}
              isEmpty={!posture || posture.rows.length === 0}
              emptyTitle="No control testing recorded"
              emptyDescription="Domain scores appear once compliance controls are tested in this partition."
              emptyIcon={ShieldAlert}
              scroll
            >
              <FigureTable
                columns={[
                  { key: 'domain', label: 'Domain', align: 'left' },
                  { key: 'controls', label: 'Controls', align: 'right' },
                  { key: 'remediation', label: 'Remed', align: 'right' },
                  { key: 'evidence', label: 'Ev gaps', align: 'right' },
                  { key: 'critical', label: 'Critical', align: 'right' },
                  { key: 'score', label: 'Overall', align: 'right' },
                ]}
                rows={postureRows}
                showWorkspaceLink={false}
              />
            </Panel>
          </GridCell>

          <GridCell panelId="obligations-deadlines" panelLabel="Obligations & Deadlines" col={2} row={2}>
            <Panel
              title="Obligations & Deadlines"
              className="min-w-0 flex-1"
              isLoading={isLoading}
              isEmpty={!obligations || obligations.rows.length === 0}
              emptyTitle="No outstanding obligations"
              emptyDescription="Regulatory filings, licenses, reviews, training and notices are all clear or unrecorded."
              emptyIcon={ScrollText}
              skeletonRows={5}
              scroll
            >
              {obligations && (
                <MatrixPanel
                  data={{
                    columns: obligations.buckets,
                    rows: obligations.rows.map((row) => ({
                      key: row.category,
                      label: row.label,
                      cells: row.cells,
                    })),
                  }}
                />
              )}
            </Panel>
          </GridCell>

          <GridCell panelId="exceptions-deviations" panelLabel="Exceptions & Deviations" col={2} row={2}>
            <Panel
              title="Exceptions & Deviations"
              className="min-w-0 flex-1"
              isLoading={isLoading}
              isEmpty={!exceptions || exceptions.rows.length === 0}
              emptyTitle="No exceptions recorded"
              emptyDescription="Loan document deviations, policy exceptions, waivers, conflicts and privacy exceptions are all absent."
              skeletonRows={5}
              scroll
              footer={exceptions && <FooterFigures figures={[exceptions.precedentRisk]} />}
            >
              {exceptions && (
                <MatrixPanel
                  data={{
                    columns: exceptions.statuses,
                    rows: exceptions.rows.map((row) => ({
                      key: row.category,
                      label: row.label,
                      cells: row.cells,
                    })),
                  }}
                />
              )}
            </Panel>
          </GridCell>

          <GridCell panelId="audits-remediation" panelLabel="Audits, Exams & Remediation" col={2} row={2}>
            <Panel
              title="Audits, Exams & Remediation"
              className="min-w-0 flex-1"
              kicker={audits ? `${audits.rows.length} open` : undefined}
              isLoading={isLoading}
              isEmpty={!audits || (audits.rows.length === 0 && audits.summary.length === 0)}
              emptyTitle="No open examinations"
              emptyDescription="Regulatory exams, internal audits, assessments and reviews appear here while they are open."
              scroll
              footer={audits && <FooterFigures figures={audits.summary} />}
            >
              <FigureTable
                columns={[
                  { key: 'item', label: 'Item', align: 'left' },
                  { key: 'type', label: 'Type', align: 'left' },
                  { key: 'status', label: 'Status', align: 'left' },
                  { key: 'complete', label: 'Complete', align: 'right' },
                  { key: 'findings', label: 'Findings', align: 'right' },
                  { key: 'due', label: 'Due', align: 'right' },
                ]}
                rows={auditRows}
                showWorkspaceLink={false}
              />
            </Panel>
          </GridCell>
        </DashboardGrid>
      )
    }

    // kyc
    return (
      <DashboardGrid rows={4}>
        <GridCell panelId="kyc-screening" panelLabel="KYC / AML / Sanctions" col={2} row={4} colStart={1} rowStart={1}>
          <Panel
            title="KYC / AML / Sanctions"
            className="min-w-0 flex-1"
            kicker={kyc && !isBlank(kyc.coverage) ? formatFigure(kyc.coverage) : undefined}
            isLoading={isLoading}
            isEmpty={!kyc || (kyc.parties.length === 0 && kyc.screenings.length === 0)}
            emptyTitle="No screened parties"
            emptyDescription="Borrowers, sponsors, guarantors, vendors and investors appear once KYC records exist."
            emptyIcon={ShieldAlert}
            scroll
          >
            {kyc && (
              <div className="space-y-2.5">
                <div className="grid grid-cols-2 gap-3">
                  <div className="min-w-0 space-y-2">
                    <SubHeading>Parties</SubHeading>
                    <StatList figures={[...kyc.parties, kyc.coverage, kyc.eddOpen]} />
                  </div>
                  <div className="min-w-0 space-y-2">
                    <SubHeading>Screening</SubHeading>
                    <FunnelPanel
                      data={{
                        stages: kyc.screenings.map((screening) => ({
                          key: screening.label,
                          label: screening.label,
                          value: typeof screening.value === 'number' ? screening.value : null,
                          drill: screening.drill,
                        })),
                      }}
                    />
                  </div>
                </div>

                {/* A single "12 alerts open" count cannot say whether they
                    arrived this week or have sat unresolved for two months, and
                    only one of those is a finding — hence the aging row. It
                    spans the panel because four buckets do not fit a half. */}
                <div className="space-y-1">
                  <SubHeading>Open alert aging</SubHeading>
                  <StatList figures={[kyc.alertAging.openAlerts]} />
                  <MatrixPanel
                    data={{
                      columns: kyc.alertAging.buckets.map((bucket) => ({
                        key: bucket.key,
                        label: bucket.label,
                      })),
                      rows: [
                        {
                          key: 'open_alerts',
                          label: 'Days open',
                          cells: kyc.alertAging.buckets.map((bucket) => bucket.count),
                        },
                      ],
                    }}
                  />
                </div>
              </div>
            )}
          </Panel>
        </GridCell>

        <GridCell panelId="outside-counsel-budget" panelLabel="Outside Counsel & Budget" col={2} row={2} colStart={3} rowStart={1}>
          <Panel
            title="Outside Counsel & Budget"
            className="min-w-0 flex-1"
            isLoading={isLoading}
            isEmpty={!counsel || (counsel.rows.length === 0 && counsel.summary.length === 0)}
            emptyTitle="No counsel invoices"
            emptyDescription="Spend, budget and rate compliance appear once invoices are recorded for the plan year."
            emptyIcon={Inbox}
            scroll
            bodyClassName="space-y-2.5"
          >
            {counsel && (
              <>
                <StatList figures={counsel.summary} />
                <div className="space-y-1">
                  <SubHeading>Spend vs budget</SubHeading>
                  <div className="h-[128px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart
                        data={counsel.spendVsBudget.points}
                        margin={{ top: 4, right: 2, bottom: 0, left: -12 }}
                      >
                        <CartesianGrid {...chartGridProps()} />
                        <XAxis
                          dataKey="label"
                          {...chartAxisProps(9)}
                          interval="preserveStartEnd"
                        />
                        <YAxis
                          width={46}
                          {...chartAxisProps(9)}
                          tickFormatter={(value: number) =>
                            formatFigure({ label: 'Spend', value, format: 'currencyM' })
                          }
                        />
                        <Tooltip
                          {...CHART_TOOLTIP_PROPS}
                          formatter={(value: number, name: string): [string, string] => [
                            formatFigure({ label: name, value, format: 'currency' }),
                            name,
                          ]}
                        />
                        {/* No iconType override: the bar needs a swatch and the
                            plan lines need a line, and one icon cannot be both. */}
                        <Legend wrapperStyle={CHART_LEGEND_STYLE} />
                        <Bar
                          dataKey="spend"
                          name="Spend"
                          fill={chartSeriesColor(3)}
                          radius={[2, 2, 0, 0]}
                          isAnimationActive={false}
                        />
                        {SPEND_LINES.map((series) => (
                          <Line
                            key={series.key}
                            type="monotone"
                            dataKey={series.key}
                            name={series.label}
                            stroke={series.color}
                            strokeWidth={1.4}
                            strokeDasharray={series.dash}
                            dot={false}
                            // A month with no budget line breaks the line rather
                            // than being bridged over — the gap is the finding.
                            connectNulls={false}
                            isAnimationActive={false}
                          />
                        ))}
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                {counsel.rows.length > 0 && (
                  <div className="space-y-1">
                    <SubHeading>Top firms YTD</SubHeading>
                    <FirmList rows={counsel.rows} />
                  </div>
                )}
              </>
            )}
          </Panel>
        </GridCell>

        <GridCell panelId="todays-governance" panelLabel="Today's Governance" col={2} row={2} colStart={3} rowStart={3}>
          <Panel
            title="Today's Governance"
            className="min-w-0 flex-1"
            kicker={governance ? `${governance.rows.length} open` : undefined}
            isLoading={isLoading}
            isEmpty={(governance?.rows.length ?? 0) === 0}
            emptyTitle="Docket is clear"
            emptyDescription="No committee items, exception approvals, filings or privilege reviews are awaiting a decision."
            skeletonRows={4}
            scroll
          >
            {governance && <GovernanceTiles rows={governance.rows} />}
          </Panel>
        </GridCell>
      </DashboardGrid>
    )
  }

  return (
    // No kicker: the platform header one row above already renders the module
    // label 'Risk Mgmt'. A fourth restatement costs a row of a page-fill layout.
    <DashboardShell
      dashboardId="legal"
      dashboardView={active}
      title="Risk Mgmt"
      subtitle="Enterprise legal risk, regulatory oversight and transaction control."
      partition={board.partition}
      dataUpdatedAt={board.dataUpdatedAt}
      isFetching={board.isFetching}
      error={error}
      onRetry={retry}
      tabs={<DashboardTabs tabs={TABS} active={active} onChange={setActive} />}
      fill
    >
      <div className="flex h-full min-h-0 flex-col gap-2">
        {error ? null : (
          <>
            {/* Headline figures — persistent across every tab. Board-level
                fetch failures stay in DashboardShell's compact banner; this
                strip must not repeat "Could not load this panel". */}
            <KpiStrip
              tiles={headline?.tiles ?? []}
              columnsClassName="grid-cols-4 xl:grid-cols-8"
              isLoading={isLoading}
              className="flex-shrink-0"
            />

            {/* The annual compliance plan band rides the overview tab only. */}
            {active === 'overview' && (
              <div className="surface-panel flex min-w-0 flex-shrink-0 flex-wrap items-center gap-x-4 gap-y-2 rounded-md px-3 py-2">
                <div className="flex-shrink-0">
                  <SubHeading>
                    Compliance plan {planProgress ? planProgress.planYear : ''}
                  </SubHeading>
                  <div className="mt-0.5 font-mono text-[13px] tabular-nums text-stone-100">
                    {planProgress ? (
                      <FigureValue figure={planProgress.progress} />
                    ) : (
                      <span className="text-stone-600">{NO_VALUE}</span>
                    )}
                  </div>
                </div>
                <div className="chart-viz-track h-2 min-w-[160px] flex-1 rounded-full">
                  {planPct != null && (
                    <div
                      className="chart-viz-bar chart-viz-bar--rounded h-full"
                      style={{ width: `${planPct}%`, background: chartSemanticProgressColor(planPct) }}
                    />
                  )}
                </div>
                <div className="flex-shrink-0 text-[10px] text-stone-500">
                  {planProgress ? (
                    <FooterFigures figures={[planProgress.complete, planProgress.total]} />
                  ) : (
                    <span>Plan progress has not been returned for this partition.</span>
                  )}
                </div>
              </div>
            )}

            {renderActiveGrid()}
          </>
        )}
      </div>
    </DashboardShell>
  )
}
